import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthResponseType } from '@app/models/auth/auth-response-type.enum';
import { AuthType } from '@app/models/auth/auth-type.enum';
import { SSOProvider } from '@app/models/auth/sso-provider.enum';
import { SessionEventType } from '@app/models/session-log/session-event-type.model';
import { AuthAPIService } from '@app/shared/auth/auth.api.service';
import { Angulartics2Amplitude } from 'angulartics2/amplitude';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SwalUtils } from '../../../shared/utils/swal.utils';
import { I18nService } from '@app/i18n/i18n.service';
import { IState } from '@app/models/state/state.model';
import { CommonMessages } from '@app/constants/common.messages';
import { AuthMessages } from '@app/auth/auth.messages';
import { Globals } from '@app/shared/globals/globals';
import { SessionLogService } from '@app/shared/session-log/session-log.service';
import { environment } from '@env/environment';
import { AuthResponse } from '@app/models/auth/auth-response.model';
import { UserAPIService } from '@app/shared/api/user.api.service';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';

interface PageState extends IState {
  submitted: boolean;
  showManualLogin: boolean;
}

@Component({
  selector: 'app-login-component',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnDestroy, OnInit {
  private readonly ngUnsubscribe$ = new Subject<void>();
  public readonly eCommonMessages = CommonMessages;
  public readonly eAuthMessages = AuthMessages;
  public readonly eSSOProvider = SSOProvider;
  public readonly eButtonType = ButtonType;
  public readonly eAuthType = AuthType;

  @ViewChild('buttonSSOMicrosoft', {static: false}) buttonSSOMicrosoft!: ElementRef;
  @ViewChild('buttonSSOGoogle', {static: false}) buttonSSOGoogle!: ElementRef;

  state: PageState;

  loginForm: FormGroup;

  ssoError: string | null;
  redirect: string | null;
  shouldRefreshMicrosoftToken: boolean;

  googleCalendarScope: string;
  allowedSignInMethods: {
    [key in AuthType]: boolean;
  };

  get controlEmail(): FormControl {
    return this.loginForm.controls.email as FormControl;
  }

  get controlPassword(): FormControl {
    return this.loginForm.controls.password as FormControl;
  }

  constructor(
      private authService: AuthAPIService,
      private router: Router,
      public globals: Globals,
      private route: ActivatedRoute,
      private angulartics2Amplitude: Angulartics2Amplitude,
      private sessionLogService: SessionLogService,
      private i18nService: I18nService,
      private swalUtils: SwalUtils,
      private userAPIService: UserAPIService
  ) {
    this.allowedSignInMethods = {
      [AuthType.EMAIL_PASSWORD]: true,
      [AuthType.SSO_GOOGLE]: true,
      [AuthType.SSO_MICROSOFT]: true,
    };

    this.state = {
      loading: false,
      error: false,
      errorMessage: '',
      submitted: false,
      showManualLogin: false
    };

    this.loginForm = this.initForm();

    // Fetch the sso error if it exists
    this.ssoError = localStorage.getItem('sso_error');
    this.shouldRefreshMicrosoftToken = (localStorage.getItem('refresh_microsoft_token') === 'true');

    // Check if a redirect happened
    const redirect = this.route.snapshot.queryParamMap.get('redirect');
    if (redirect === null) {
      this.redirect = null;
    } else {
      this.redirect = decodeURI(redirect);
    }

    this.googleCalendarScope = '+https://www.googleapis.com/auth/calendar';
  }

  ngOnInit() {
    if (this.ssoError) {
      const timestamp = +this.ssoError.split(':')[0];
      const message = this.ssoError.split(':')[1];
      // Check if error is 30 seconds old or less
      if (Date.now() - timestamp < 30 * 1000) {
        this.swalUtils.displayErrorSwal({
          title: 'Error',
          text: message,
          confirmButtonColor: '#2DA1B3',
        });
      }
      localStorage.removeItem('sso_error');
    }

    const allowManualLogin = this.route.snapshot.queryParamMap.get('allowManualLogin');
    this.allowedSignInMethods[AuthType.EMAIL_PASSWORD] = (allowManualLogin === 'false') ? false : true;
    const allowGoogleLogin = this.route.snapshot.queryParamMap.get('allowGoogleLogin');
    this.allowedSignInMethods[AuthType.SSO_GOOGLE] = (allowGoogleLogin === 'false') ? false : true;
    const allowMicrosoftLogin = this.route.snapshot.queryParamMap.get('allowMicrosoftLogin');
    this.allowedSignInMethods[AuthType.SSO_MICROSOFT] = (allowMicrosoftLogin === 'false') ? false : true;

    const attemptLoginWith = this.route.snapshot.queryParamMap.get('attemptLoginWith');
    if (attemptLoginWith) {
      switch(attemptLoginWith) {
        case 'google':
          this.ssoLogin(SSOProvider.GOOGLE);
          break;
        case 'microsoft':
          this.ssoLogin(SSOProvider.MICROSOFT);
          break;
      }
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.unsubscribe();
  }

  initForm(): FormGroup {
    const formGroup = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.pattern(/^(.*).@(.*).\..+$/)]),
      password: new FormControl('', Validators.required),
    });

    formGroup.controls.email.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(value => {
        if (value[value.length - 1] === ' ') {
          this.controlEmail.patchValue(this.controlEmail.value.trim());
        }
      });

    return formGroup;
  }

  login() {
    this.state.submitted = true;
    this.state.error = false;

    if (this.loginForm.invalid) {
      return;
    }

    this.state.loading = true;

    const login = this.loginForm.value as { email: string; password: string; };

    this.authService.authenticate(login).subscribe(
      (authResponse) => this.onAuthResponse(authResponse),
      (failure: HttpErrorResponse) => {
        this.state.errorMessage = failure.error;
        this.state.submitted = false;
        this.state.error = true;
        this.state.loading = false;
      }
    );
  }

  onAuthResponse(response: AuthResponse): void {
    // handle different AuthResponseTypes
    switch (response.type) {
      case AuthResponseType.SUCCESS: {
        if (response.userInit !== null) {
          localStorage.setItem('access_token', response.userInit.accessToken);
          this.globals.init(response.userInit);
          this.globals.welcomeViewed = false;
          this.globals.ssoProvider = undefined;
          this.i18nService.language = this.globals.user.configuration.preferredLocale;
          if (environment.production) {
            this.angulartics2Amplitude.setUsername(this.globals.user.email);
          }
          this.sessionLogService.log(SessionEventType.SESSION_START_LOGIN);
          this.globals.welcomeViewed = false;
          // if redirect set, go to redirect

          this.userAPIService.setTimezone(this.globals.user).subscribe(
            (user) => {
              this.globals.updateUser(user);
              this.router.navigateByUrl(this.redirect ? this.redirect : '/');
            }
          );
        }
        break;
      }
      case AuthResponseType.LOGIN_METHOD_DISABLED: {
        // build containers
        const container = document.createElement('div');
        const header = document.createElement('h4');
        header.innerHTML = 'Please sign in with SSO';
        header.style.fontWeight = '700';
        header.style.color = '#012139';
        const description = document.createElement('p');
        description.innerHTML = 'Email and password login has been disabled for your company. Please use one of the options below.';

        // build button containers
        const buttonContainer = document.createElement('div');
        buttonContainer.style.display = 'flex';
        buttonContainer.style.justifyContent = 'center';
        buttonContainer.style.marginTop = '15px';
        if (response.validAuthTypes.includes(AuthType.SSO_MICROSOFT)) {
          buttonContainer.appendChild(this.buttonSSOMicrosoft.nativeElement);
        }
        if (response.validAuthTypes.includes(AuthType.SSO_GOOGLE)) {
          const spacer = document.createElement('span');
          spacer.style.marginRight = '15px';
          buttonContainer.appendChild(spacer);
          buttonContainer.appendChild(this.buttonSSOGoogle.nativeElement);
        }

        // append
        container.appendChild(
          header,
        );
        container.appendChild(
          description,
        );
        container.appendChild(
          buttonContainer,
        );

        this.swalUtils.displayBaseSwal({
          title: '',
          // text: response.message,
          html: container,
          showConfirmButton: false,
        });
        this.state.error = false;
        this.state.submitted = false;
        this.state.errorMessage = '';
        this.state.loading = false;
        break;
      }
      case AuthResponseType.LOGIN_DISABLED:
      case AuthResponseType.ALL_LOGIN_METHODS_DISABLED: {
        this.swalUtils.displayWarning({
          title: 'Could not log you in',
          text: response.message,
        });
        this.state.error = false;
        this.state.submitted = false;
        this.state.errorMessage = '';
        this.state.loading = false;
        break;
      }
      default:
        this.state.errorMessage = response.message;
        this.state.submitted = false;
        this.state.error = true;
        this.state.loading = false;
        break;
    }
  }

  ssoLogin(provider: SSOProvider) {
    switch (provider) {
      case SSOProvider.MICROSOFT:
        document.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=select_account&client_id=${environment.sso.microsoft.clientId}&response_type=code&redirect_uri=${environment.sso.redirectUri}&scope=openid+email+profile+offline_access+calendars.readwrite&state=${SSOProvider.MICROSOFT}`;

        // if (this.shouldRefreshMicrosoftToken) {
        //   FIXME: Was going to add prompt=consent to the url but we already have prompt=select_account so will test that first
        //   document.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=select_account&client_id=${environment.sso.microsoft.clientId}&response_type=code&redirect_uri=${environment.sso.redirectUri}&scope=openid+email+profile+offline_access+calendars.readwrite&state=${SSOProvider.MICROSOFT}"`;
        // } else {
        //   document.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=select_account&client_id=${environment.sso.microsoft.clientId}&response_type=code&redirect_uri=${environment.sso.redirectUri}&scope=openid+email+profile+offline_access+calendars.readwrite&state=${SSOProvider.MICROSOFT}`;
        // }
        break;
      case SSOProvider.GOOGLE:
        document.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${environment.sso.google.clientId}&redirect_uri=${environment.sso.redirectUri}&scope=openid+email+profile${this.googleCalendarScope}&response_type=code&state=${SSOProvider.GOOGLE}&access_type=offline`;
        break;
      default:
        break;
    }
  }

  startManualLogin(): void {
    this.state.showManualLogin = true;
  }
}
